import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { IClosedContract, IContract, IContractFileHistoryItem, IContractModel, IContractOdata, IContractStatusCounts, IDataMockup, TModel } from './entities/contracts.interface';
import DataSource from 'devextreme/data/data_source';
import { EMPTY, ObservableInput, filter, forkJoin, from, pipe, switchMap, tap } from 'rxjs';
import { inject } from '@angular/core';
import { ContractsService } from './infra/contracts.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { DownloadFileService } from '@shared/utils/download-file';
import { DomSanitizer } from '@angular/platform-browser';
import { ErrorStore } from '@application/errors/data-access';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { DialogStore } from '@shared/components/ui/dialog/data-access';
import { StorageService } from '@shared/storage/data-access';
import { UserService } from '@domains/person/user/data';
import { ESubscriptionsByPersonFieldFilter, ProfileService, ProfileSubscriptionListItem, ProfileSubscriptionService, TProfile } from '@domains/person/profile/data';
import { selectContractFields, IContractNotSentResult } from './entities/contracts.model';
import { PermissionNumber, SelfUserStore, SystemModulesStore, SystemModules } from '@domains/person/user/self/data';
import { AlertStore } from '@alert/data-access';
import { APP_CONFIG } from '@application/config';
import { EventProgressStore } from '@shared/components/ui/event-progress/data-access';
import { ClientNotificationStore, EClientNotificationsEventTypes, IClientNotificationStore } from '@shared/push-notification';
import { getDateTime } from '@shared/utils';
import { FormattingService } from '@settings/data-access';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';
const SIGN_CUT_OFF_DATE_YEAR = 2025;
const SIGN_CUT_OFF_DATE_MONTH = 11;
const SIGN_CUT_OFF_DATE_DAY = 3;

export interface IContractStore {
  templateModel: string | null;
  mockupData: IDataMockup | null;
  isViewModalOpen: boolean;
  isSelectModelModalOpen: boolean;
  newContractModal: {
    person?: { PersonId: string; FullDisplayName: string };
    model?: TModel;
  } | null;
  isClosedContractModalOpen: boolean;
  isContractDetailsModalOpen: boolean;
  contractModalData: IContract | null;
  currentModelData: IContractModel | null;
  contractsDataSource: DataSource;
  suretiesDataSource: DataSource;
  creditorsDataSource: DataSource;
  contracts: IContractOdata[];
  allSubscriptionsByPersonId: Record<string, { PersonSubscriptionId: string; Description: string; }[]>;
  templateModelFileById: Record<string, string>;
  statusCounts: IContractStatusCounts | null;
  globalStatusCounts: IContractStatusCounts | null;
  modelRawData: { ModelId: string; ModelDescription: string }[] | null;
  fileHistory: IContractFileHistoryItem[] | null;
  contractsNotSentResults: IContractNotSentResult[] | null;
  isContractsNotSentModalOpen: boolean;
  /** Visibility of the "Resolver Pendências" guided-action drawer (opened from the page or the not-sent modal). */
  isResolvePendingOpen: boolean;
  /** Visibility of the "Corrigir dados para envio" quick-fix modal (opened from the drawer or the not-sent modal). */
  isFixRegistrationsOpen: boolean;
  blockedContracts: IContractOdata[] | null;
  /** Recipient person loaded (without includes) for the quick-fix flow, to read the current Email. */
  recipientProfile: TProfile | null;
  /**
   * Assinaturas ativas da pessoa selecionada no modal de novo contrato.
   *
   * Vem de `Subscription/GetSubscriptionsByPerson` (REST) e não da view OData
   * `Subscriptions`: a `SubscriptionView` do OData expõe 21 propriedades e
   * nenhuma delas é `IsDependent` / `HolderPersonId` / `IsHolder`, ou seja, um
   * dependente não tem linha lá e nenhum `$filter` conseguiria alcançá-lo.
   */
  personSubscriptions: ProfileSubscriptionListItem[] | null;
}

export const initialContractStore: IContractStore = {
  templateModel: null,
  mockupData: null,
  isViewModalOpen: false,
  contractModalData: null,
  isSelectModelModalOpen: false,
  newContractModal: null,
  isClosedContractModalOpen: false,
  isContractDetailsModalOpen: false,
  currentModelData: null,
  contracts: null,
  allSubscriptionsByPersonId: {},
  templateModelFileById: {},
  contractsDataSource: null,
  suretiesDataSource: null,
  creditorsDataSource: null,
  statusCounts: null,
  globalStatusCounts: null,
  modelRawData: null,
  fileHistory: null,
  contractsNotSentResults: null,
  isContractsNotSentModalOpen: false,
  isResolvePendingOpen: false,
  isFixRegistrationsOpen: false,
  blockedContracts: null,
  recipientProfile: null,
  personSubscriptions: null,
};

export const ContractStore = signalStore(
  { providedIn: 'root' },
  withState(initialContractStore),
  withReqState('loadClosedUser', 'closeContract', 'loadModelsBaseData', 'loadModel', 'generateLinkToSign', 'contractPDF', 'deleteModel', 'Contract', 'deleteContract', 'updateModel', 'createModel', 'sendMailNotification', 'sendRemindersUnsignedContracts', 'sendToSignsNotSends', 'sendToSignsPending', 'getTemplateModelFile', 'loadContract', 'loadStatusCounts', 'loadGlobalStatusCounts', 'loadModelRawData', 'loadFileHistory', 'loadResultsContractsNotSents', 'loadBlockedContracts', 'loadRecipientProfile', 'loadPersonSubscriptions', 'duplicateModel'),
  withMethods((
    store,
    errorStore = inject(ErrorStore),
    cService = inject(ContractsService),
    downloadService = inject(DownloadFileService),
    sanitizer = inject(DomSanitizer),
    translateService = inject(TranslateService),
    dialogStore = inject(DialogStore),
    storageService = inject(StorageService),
    userService = inject(UserService),
    profileService = inject(ProfileService),
    profileSubscriptionService = inject(ProfileSubscriptionService),
    selfUserStore = inject(SelfUserStore),
    systemModulesStore = inject(SystemModulesStore),
    alertStore = inject(AlertStore),
    eventProgressStore = inject(EventProgressStore),
    externalUrl = inject(APP_CONFIG).externalUrl,
    formattingService = inject(FormattingService)
  ) => {

    const ensurePermission = async (
      permission: PermissionNumber,
      translationKey: string,
    ): Promise<boolean> => {
      const hasPermission = await selfUserStore.ensurePermissions([permission], { interactive: false });

      if (hasPermission) {
        return true;
      }

      return selfUserStore.ensurePermissions([permission], {
        title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.TITLE`,
        message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.${translationKey}.MESSAGE`,
      });
    };

    const guardPermission = <T>(
      permission: PermissionNumber,
      translationKey: string,
      onDenied: () => void,
      work: () => ObservableInput<T>,
    ) => from(ensurePermission(permission, translationKey)).pipe(
      switchMap((hasPermission) => {
        if (!hasPermission) {
          onDenied();
          return EMPTY;
        }

        return work();
      })
    );

    const getSignedOnLabel = (signedOn: string | Date): string => {
      const date = formattingService.formatDate(signedOn, { year: 'numeric', month: 'short', day: 'numeric' });
      return translateService.instant('CONTRACTS.SIGNED_ON', { date });
    }

    const getContractWithSigners = <T extends IContract | IContractOdata>(contract: T): T => {
      const signerCandidates = [
        { fullName: contract.SignerFullName, signedOn: contract.SignerSignedOn },
        { fullName: contract.CompanySignerFullName, signedOn: contract.CompanySignedOn },
        { fullName: contract.SuretyFullName, signedOn: contract.EndorsedOn },
        { fullName: contract.WitnessOneSignerFullName, signedOn: contract.WitnessOneSignedOn },
        { fullName: contract.WitnessTwoSignerFullName, signedOn: contract.WitnessTwoSignedOn },
      ];

      const signers = signerCandidates
        .filter(signer => signer.fullName && signer.signedOn)
        .map(signer => ({
          title: signer.fullName,
          content: getSignedOnLabel(signer.signedOn),
        }));

      return {
        ...contract,
        signers,
      };
    }

    const loadClosedUser = rxMethod<string>(pipe(
      tap(() => patchState(store, { ...setPropLoading('loadClosedUser') })),
      switchMap((userId) => userService.getUser(userId).pipe(
        tapResponse({
          next: (user) => {
            patchState(store, {
              contractModalData: {
                ...store.contractModalData(),
                closedByFullName: user?.FirstName,
                closedByEmail: user?.Email,
              },
              ...setPropLoaded('loadClosedUser')
            });
          },
          error: (error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, { ...setPropError('loadClosedUser', error) });
          }
        })
      ))
    ));

    const loadFileHistory = rxMethod<IContract['ContractId']>(pipe(
      filter((contractId): contractId is string => !!contractId),
      tap(() => patchState(store, { fileHistory: null, ...setPropLoading('loadFileHistory') })),
      switchMap((contractId) => cService.getFileHistory(contractId).pipe(
        tapResponse({
          next: (fileHistory) => patchState(store, { fileHistory, ...setPropLoaded('loadFileHistory') }),
          error: (error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, { ...setPropError('loadFileHistory', error) });
          }
        })
      ))
    ));

    const loadContract = rxMethod<IContract['ContractId']>(pipe(
      tap(() => patchState(store, { contractModalData: null, fileHistory: null, ...setPropLoading('loadContract'), ...setPropInit('loadFileHistory') })),
      switchMap(id =>
        guardPermission(
          PermissionNumber.ContratoObterPorId,
          'CONTRACT_VIEW',
          () => patchState(store, { ...setPropError('loadContract', new Error('ALERT.ERROR_401')) }),
          () => cService.getContract(id).pipe(
            tapResponse({
              next: (contractModalData) => {
                contractModalData = getContractWithSigners(contractModalData);
                if (contractModalData.ClosedBy) loadClosedUser(contractModalData.ClosedBy);
                patchState(store, { contractModalData, ...setPropLoaded('loadContract') })
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('loadContract', error) });
              }
            })
          )
        )
      )
    ));

    const deleteContract = rxMethod<IContract['ContractId']>(pipe(
      tap(() => patchState(store, { ...setPropLoading('deleteContract') })),
      switchMap(id =>
        guardPermission(
          PermissionNumber.ContratoRemover,
          'CONTRACT_DELETE',
          () => patchState(store, { ...setPropError('deleteContract', new Error('ALERT.ERROR_401')) }),
          () => cService.deleteContract(id).pipe(
            tapResponse({
              next: () => {
                patchState(store, { isViewModalOpen: false, contractModalData: null, ...setPropLoaded('deleteContract') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('deleteContract', error));
              }
            })
          )
        )
      )
    ));

    const sendToSignsNotSends = rxMethod<void>(pipe(
      tap(() => patchState(store, { ...setPropLoading('sendToSignsNotSends') })),
      switchMap(() => cService.sendToSignsNotSends().pipe(
        tapResponse({
          next: (response) => {
            if (response?.EventId) {
              eventProgressStore.addEvent(response.EventId, undefined, 'CONTRACTS.EVENT_PROGRESS_TITLE');
            }
            patchState(store, { ...setPropLoaded('sendToSignsNotSends') });
          },
          error: (error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, setPropError('sendToSignsNotSends', error));
          }
        })
      ))
    ));

    const sendToSignsPending = rxMethod<void>(pipe(
      tap(() => patchState(store, { ...setPropLoading('sendToSignsPending') })),
      switchMap(() => cService.sendToSignsPending().pipe(
        tapResponse({
          next: (response) => {
            if (response?.EventId) {
              eventProgressStore.addEvent(response.EventId, undefined, 'CONTRACTS.EVENT_PROGRESS_TITLE');
            }
            patchState(store, { ...setPropLoaded('sendToSignsPending') });
          },
          error: (error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, setPropError('sendToSignsPending', error));
          }
        })
      ))
    ));

    const sendRemindersUnsignedContracts = rxMethod<void>(pipe(
      tap(() => patchState(store, { ...setPropLoading('sendRemindersUnsignedContracts') })),
      switchMap(() => cService.sendRemindersUnsignedContracts().pipe(
        tapResponse({
          next: () => patchState(store, { ...setPropLoaded('sendRemindersUnsignedContracts') }),
          error: (error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, setPropError('sendRemindersUnsignedContracts', error));
          }
        })
      ))
    ));

    /** Extracts the `EventId` from an `onCentralNotificationContract` payload, but only when it comes from the contract-send batch. */
    const extractContractSendEventId = (notification: IClientNotificationStore['notifications']): string | null => {
      if (notification?.eventName !== EClientNotificationsEventTypes.onCentralNotificationContract) return null;
      const { Origin, Content } = notification.eventData ?? {};
      let content = Content;
      if (typeof content === 'string') {
        try { content = JSON.parse(content); } catch { content = null; }
      }
      const isContractSendEvent = Origin === EClientNotificationsEventTypes.onContractEventNotification
        || content?.EventName === EClientNotificationsEventTypes.onContractEventNotification;
      return isContractSendEvent ? (content?.EventId ?? null) : null;
    };

    const loadResultsContractsNotSents = rxMethod<{ eventId: string; notifyWhenEmpty?: boolean }>(pipe(
      filter((params): params is { eventId: string; notifyWhenEmpty?: boolean } => !!params?.eventId),
      tap(() => patchState(store, { contractsNotSentResults: null, ...setPropLoading('loadResultsContractsNotSents') })),
      switchMap(({ eventId, notifyWhenEmpty }) => cService.getResultsContractsNotSents(eventId).pipe(
        tapResponse({
          next: (contractsNotSentResults) => {
            const hasResults = !!contractsNotSentResults?.length;
            if (!hasResults && notifyWhenEmpty) {
              alertStore.create({
                title: 'CONTRACTS.NOT_SENT_RESULTS.ALL_SENT_SUCCESS_TITLE',
                message: 'CONTRACTS.NOT_SENT_RESULTS.ALL_SENT_SUCCESS_MESSAGE',
                type: 'info'
              });
            }
            patchState(store, {
              contractsNotSentResults,
              isContractsNotSentModalOpen: hasResults,
              ...setPropLoaded('loadResultsContractsNotSents')
            });
          },
          error: (error: HttpErrorResponse) => {
            errorStore.setHttpError(error);
            patchState(store, setPropError('loadResultsContractsNotSents', error));
          }
        })
      ))
    ));

    /** Reacts to `onCentralNotificationContract` events and loads the not-sent results for the finished batch. */
    const watchCentralContractNotification = rxMethod<IClientNotificationStore['notifications']>(pipe(
      tap((notification: IClientNotificationStore['notifications']) => {
        const eventId = extractContractSendEventId(notification);
        if (eventId) loadResultsContractsNotSents({ eventId });
      }),
    ));

    const closeContract = rxMethod<IClosedContract>(pipe(
      tap(() => patchState(store, { ...setPropLoading('closeContract') })),
      switchMap((data) =>
        guardPermission(
          PermissionNumber.ContratoRemover,
          'CONTRACT_DELETE',
          () => patchState(store, { ...setPropError('closeContract', new Error('ALERT.ERROR_401')) }),
          () => cService.closeContract(data).pipe(
            tapResponse({
              next: (contractModalData) => {
                patchState(store, { isClosedContractModalOpen: false, contractModalData, ...setPropLoaded('closeContract') });
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, { ...setPropError('closeContract', error) });
              }
            })
          )
        )
      )
    ));

    const allowsOnlineContractSigning = (): boolean =>
      systemModulesStore.isModuleAllowed(SystemModules.Contratos) || selfUserStore.isAdminScope();

    /** Cut-off date rule for POST `/ContractsSign/SendToSign` (after Contratos module check). */
    const validateStartedOnForSendToSign = (startedOn?: string): boolean => {
      const cutOffDate = DateTime.fromObject({
        year: SIGN_CUT_OFF_DATE_YEAR,
        month: SIGN_CUT_OFF_DATE_MONTH,
        day: SIGN_CUT_OFF_DATE_DAY,
      });

      const date = getDateTime(startedOn);

      if (!startedOn || !date?.isValid || date < cutOffDate) {
        alertStore.create({
          title: 'ALERT.GENERIC_ERROR_TITLE',
          message: 'CONTRACTS.SIGN_ALERT.ALLOWED_DATE_MESSAGE',
          type: 'warning'
        });
        return false;
      }

      return true;
    }

    const canBeSignIn = (startedOn?: string): boolean => {
      if (!systemModulesStore.isModuleAllowed(SystemModules.Contratos)) {
        alertStore.create({
          title: 'ALERT.GENERIC_ERROR_TITLE',
          message: 'CONTRACTS.SIGN_ALERT.HAS_PERMISSION_MESSAGE',
          type: 'warning'
        });
        return false;
      }
      return validateStartedOnForSendToSign(startedOn);
    };

    const getLinkToSignIn = (integratorContractKey: string, integratorRequestSignatureKey: string): string => {
      if (!integratorContractKey || !integratorRequestSignatureKey) return null;
      const base = externalUrl?.sign_view;
      const contractKey = encodeURIComponent(integratorContractKey);
      const signingKey = encodeURIComponent(integratorRequestSignatureKey);
      return `${base}/view/${contractKey}?signingKey=${signingKey}`;
    }

    return {
      canBeSignIn,
      getLinkToSignIn,
      getContracts: (personId?: string) => {
        const params = {
          $select: selectContractFields,
        };
        if (personId) params['$filter'] = `PersonId eq ${personId}`;
        patchState(store, {
          contractsDataSource: cService.getContractsFromOdata(params, {
            permissions: [PermissionNumber.ContratoObterTodos],
            translationKey: 'CONTRACT_LIST',
          })
        });
      },
      getSureties: (params?: any) => {
        patchState(store, { suretiesDataSource: cService.getSuretiesFromOdata(params) });
      },
      getCreditors: (params?: any) => {
        patchState(store, { creditorsDataSource: cService.getCreditorsFromOdata(params) });
      },
      resetSuretiesState: () => {
        patchState(store, { suretiesDataSource: null, creditorsDataSource: null });
      },
      setNewContractModal: (payload: IContractStore['newContractModal']) => {
        patchState(store, { newContractModal: payload ? { ...(store.newContractModal() || {}), ...payload } : null });
      },
      showModal: (isOpened: boolean, person: { PersonId: string; FullDisplayName: string } = null) => {
        patchState(store, { isSelectModelModalOpen: isOpened, newContractModal: person ? { person } : null });
      },
      openContractViewer: async (id: IContract['ContractId']) => {
        if (await ensurePermission(PermissionNumber.ContratoObterPorId, 'CONTRACT_VIEW')) {
          loadContract(id);
          patchState(store, { isViewModalOpen: true });
        }
      },
      closeViewContractModal: () => {
        patchState(store, { isViewModalOpen: false });
      },
      openClosedContractModal: (id: IContract['ContractId']) => {
        if (id !== store.contractModalData()?.ContractId) loadContract(id);
        patchState(store, { isClosedContractModalOpen: true });
      },
      closeClosedContractModal: () => {
        patchState(store, { isClosedContractModalOpen: false });
      },
      openContractDetailsModal: async (id: IContract['ContractId']) => {
        if (await ensurePermission(PermissionNumber.ContratoObterPorId, 'CONTRACT_VIEW')) {
          loadContract(id);
          patchState(store, { isContractDetailsModalOpen: true });
        }
      },
      closeContractDetailsModal: () => {
        patchState(store, { isContractDetailsModalOpen: false, fileHistory: null, ...setPropInit('loadFileHistory') });
      },
      loadModelsBaseData: rxMethod<void>(pipe(
        tap(() => patchState(store, {
          templateModel: null,
          mockupData: null,
          ...setPropLoading('loadModelsBaseData')
        })),
        switchMap(
          () => forkJoin(
            [cService.getTemplateModel(), cService.getDataMockup()]).pipe(
              tapResponse({
                next: ([template, mockup]) => {
                  return patchState(store, {
                    templateModel: template,
                    mockupData: mockup,
                    ...setPropLoaded('loadModelsBaseData')
                  });
                },
                error: (error) => patchState(store, setPropError('loadModelsBaseData', error))
              })
            )
        )
      )),
      loadModel: rxMethod<IContractModel['ModelId']>(pipe(
        tap(() => patchState(store, { currentModelData: null, ...setPropLoading('loadModel') })),
        switchMap(id => cService.getModelById(id).pipe(
          tapResponse({
            next: (currentModelData) => patchState(store, { currentModelData, ...setPropLoaded('loadModel') }),
            error: (error) => patchState(store, { ...setPropError('loadModel', error) }),
          })
        ))
      )),
      createModel: rxMethod<Partial<IContractModel>>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createModel') })),
        switchMap(data => cService.createModel(data).pipe(
          tapResponse({
            next: (currentModelData) => patchState(store, { currentModelData, ...setPropLoaded('createModel') }),
            error: (error) => patchState(store, { ...setPropError('createModel', error) }),
          })
        ))
      )),
      updateModel: rxMethod<Partial<IContractModel>>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateModel') })),
        switchMap(data => cService.updateModel(data).pipe(
          tapResponse({
            next: (currentModelData) => patchState(store, { currentModelData, ...setPropLoaded('updateModel') }),
            error: (error) => patchState(store, { ...setPropError('updateModel', error) }),
          })
        ))
      )),
      duplicateModel: rxMethod<{ id: IContractModel['ModelId']; description: string }>(pipe(
        tap(() => patchState(store, { ...setPropLoading('duplicateModel') })),
        switchMap(({ id, description }) => cService.getModelById(id).pipe(
          switchMap((source) => cService.createModel({
            Description: description,
            Active: source.Active,
            Content: source.Content,
          })),
          tapResponse({
            next: (currentModelData) => patchState(store, { currentModelData, ...setPropLoaded('duplicateModel') }),
            error: (error) => patchState(store, { ...setPropError('duplicateModel', error) }),
          })
        ))
      )),
      deleteModel: rxMethod<IContractModel['ModelId']>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteModel') })),
        switchMap(id => cService.deleteModel(id).pipe(
          tapResponse({
            next: () => patchState(store, { currentModelData: null, ...setPropLoaded('deleteModel') }),
            error: (error: HttpErrorResponse) => {
              patchState(store, { ...setPropError('deleteModel', error) });
            }
          }),
        ))
      )),
      blankModel: () => {
        patchState(store, { currentModelData: null }, setPropInit('getTemplateModelFile', 'loadModelsBaseData', 'loadModel', 'createModel', 'updateModel', 'deleteModel'))
      },
      loadContract: (id: IContract['ContractId']) => loadContract(id),
      loadFileHistory,
      loadPDFContract: rxMethod<void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('contractPDF') })),
        switchMap(() => cService.getPDFByUrl(store.contractModalData()?.ContractFile?.Url).pipe(
          tapResponse({
            next: (pdf) => {
              const pdfObject = URL.createObjectURL(pdf);
              const secureObj = sanitizer.bypassSecurityTrustResourceUrl(pdfObject);
              patchState(store, {
                contractModalData: {
                  ...store.contractModalData(),
                  pdfData: secureObj
                },
                ...setPropLoaded('contractPDF')
              });
            },
            error: (error: HttpErrorResponse) => {
              patchState(store, { ...setPropError('contractPDF', error) });
              errorStore.setHttpError(error);
            }
          }),
        ))
      )),
      createContract: rxMethod<Partial<IContract>>(pipe(
        tap(() => patchState(store, { ...setPropLoading('Contract') })),
        switchMap(data =>
          guardPermission(
            PermissionNumber.ContratoAdicionar,
            'CONTRACT_CREATE',
            () => patchState(store, { ...setPropError('Contract', new Error('ALERT.ERROR_401')) }),
            () => cService.addContract(data).pipe(
              tapResponse({
                next: (contractModalData) => {
                  patchState(store, { isSelectModelModalOpen: false, newContractModal: null, contractModalData, ...setPropLoaded('Contract') });
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('Contract', error) });
                },
              }),
            )
          )
        )
      )),
      updateContract: rxMethod<IContract>(pipe(
        tap(() => patchState(store, { ...setPropLoading('Contract') })),
        switchMap(data =>
          guardPermission(
            PermissionNumber.ContratoAtualizar,
            'CONTRACT_UPDATE',
            () => patchState(store, { ...setPropError('Contract', new Error('ALERT.ERROR_401')) }),
            () => cService.updateContract(data).pipe(
              tapResponse({
                next: (contractModalData) => patchState(store, { contractModalData, ...setPropLoaded('Contract') }),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, { ...setPropError('Contract', error) });
                },
              })
            )
          )
        )
      )),
      deleteContract,
      generateLinkToSign: rxMethod<Pick<IContract, 'StartedOn' | 'ContractId'>>(pipe(
        filter(({ StartedOn, ContractId }) => {
          if (ContractId == null || ContractId === '') {
            return false;
          }
     
          return canBeSignIn(StartedOn);
        }),
        tap(() => patchState(store, { ...setPropLoading('generateLinkToSign') })),
        switchMap(({ ContractId }) => cService.sendToSign(ContractId).pipe(
          tapResponse({
            next: (payload) => {
              const commonAttrs = {
                SignerFullName: payload.SignerName,
                SuretyFullName: payload.SuretySignerName,
                CompanySignerFullName: payload.CompanySignerName,
                WitnessOneSignerFullName: payload.WitnessOneSignerName,
                WitnessTwoSignerFullName: payload.WitnessTwoSignerName,
                Status: 'linktosigngenerated',
                LinkToSignGeneratedOn: DateTime.now().toISO(),
                IntegratorContractKey: payload.IntegratorContractKey,
              }
              const contracts = [...(store.contracts() || [])];
              const index = contracts.findIndex(contract => contract.ContractId['_value'] || contract.ContractId === ContractId['_value'] || ContractId);
              const contract: IContractOdata = {
                ...(contracts[index] || {}),
                ...commonAttrs,
                IntegratorRequestSignatureKey: payload.RequestSignatureKey,
                IntegratorRequestSuretyKey: payload.SuretySignatureKey,
                IntegratorRequestCompanyKey: payload.CompanySignatureKey,
                IntegratorRequestWitnessOneKey: payload.WitnessOneSignatureKey,
                IntegratorRequestWitnessTwoKey: payload.WitnessTwoSignatureKey,
              };
              contracts[index] = getContractWithSigners(contract);

              patchState(store, {
                contracts,
                contractModalData: {
                  ...(store.contractModalData() || {}),
                  ...commonAttrs,
                  RequestSignatureKey: payload.RequestSignatureKey,
                  RequestSuretyKey: payload.SuretySignatureKey,
                  RequestCompanyKey: payload.CompanySignatureKey,
                  RequestWitnessOneKey: payload.WitnessOneSignatureKey,
                  RequestWitnessTwoKey: payload.WitnessTwoSignatureKey,
                },
                ...setPropLoaded('generateLinkToSign')
              });
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, { ...setPropError('generateLinkToSign', error) });
            },
          })
        )),
      )),
      blankState: () => {
        for (const url of Object.values(store.templateModelFileById() || {})) {
          URL.revokeObjectURL(url);
        }
        patchState(store, initialContractStore)
      },
      blankPropState: () => {
        patchState(store, setPropInit(
          'loadClosedUser', 'closeContract', 'loadModelsBaseData', 'loadModel', 'generateLinkToSign', 'contractPDF', 'deleteModel', 'Contract', 'deleteContract', 'updateModel', 'createModel', 'sendMailNotification', 'getTemplateModelFile', 'loadContract', 'duplicateModel'
        ));
      },
      sendMailNotification: rxMethod<string>(pipe(
        filter((id) => {
          if (!id) return false;
          if (!allowsOnlineContractSigning()) {
            alertStore.create({
              title: 'ALERT.GENERIC_ERROR_TITLE',
              message: 'CONTRACTS.SIGN_ALERT.HAS_PERMISSION_MESSAGE',
              type: 'warning'
            });
            return false;
          }
          return true;
        }),
        tap(() => patchState(store, { ...setPropLoading('sendMailNotification') })),
        switchMap(id => guardPermission(
          PermissionNumber.ContratoObterPorId,
          'CONTRACT_VIEW',
          () => patchState(store, setPropError('sendMailNotification', new Error('ALERT.ERROR_401'))),
          () => cService.sendMailNotification(id).pipe(
            tapResponse({
              next: () => patchState(store, { ...setPropLoaded('sendMailNotification') }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('sendMailNotification', error));
              }
            })
          )
        ))
      )),

      sendRemindersUnsignedContracts: sendRemindersUnsignedContracts,

      getTemplateModelFile: rxMethod<string>(pipe(
        filter((modelId) => !!modelId && !store.templateModelFileById()[modelId]),
        tap(() => patchState(store, { ...setPropLoading('getTemplateModelFile') })),
        switchMap((modelId) => cService.getTemplateModelFile(modelId).pipe(
          tapResponse({
            next: (template) => {
              const binaryString = atob(template);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.codePointAt(i);
              }
              const blob = new Blob([bytes], { type: 'application/pdf' });
              const pdfObjectURL = URL.createObjectURL(blob);

              patchState(store, { templateModelFileById: { ...store.templateModelFileById(), [modelId]: pdfObjectURL }, ...setPropLoaded('getTemplateModelFile') })
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, { templateModelFileById: { ...store.templateModelFileById(), [modelId]: null }, ...setPropLoaded('getTemplateModelFile') });
            }
          })
        ))
      )),

      openDialogDeleteContract: (contractId: string) => {
        dialogStore.openDialog({
          title: 'CONTRACTS.DIALOG.DELETE.TITLE',
          message: 'ALERT.GENERIC_DELETE_CONFIRMATION_MESSAGE',
          closeOnSignal: [store.deleteContractLoaded, store.deleteContractError],
          primaryButton: {
            label: 'COMPONENTS.BUTTON.CANCEL',
            action: () => {
              dialogStore.closeDialog();
            },
          },
          secondaryButton: {
            label: 'COMPONENTS.BUTTON.YES_DELETE',
            type: 'destructive',
            loadingOnsignal: store.deleteContractLoading,
            action: () => {
              deleteContract(contractId);
            },
          },
        })
      },

      resetSendRemindersState: () => {
        patchState(store, { ...setPropInit('sendRemindersUnsignedContracts') });
      },

      resetSendToSignsState: () => {
        patchState(store, {
          contractsNotSentResults: null,
          isContractsNotSentModalOpen: false,
          ...setPropInit('loadResultsContractsNotSents'),
          ...setPropInit('sendToSignsNotSends'),
        });
      },

      resetSendToSignsPendingState: () => {
        patchState(store, {
          contractsNotSentResults: null,
          isContractsNotSentModalOpen: false,
          ...setPropInit('loadResultsContractsNotSents'),
          ...setPropInit('sendToSignsPending'),
        });
      },

      loadResultsContractsNotSents,

      /**
       * Loads the not-sent results for an event and opens the modal only when there
       * are results (handled by `loadResultsContractsNotSents`). Used by the explicit
       * notification click, so it passes `notifyWhenEmpty` to surface an all-sent
       * success alert instead of silently doing nothing; the background
       * `watchCentralContractNotification` flow stays silent when empty.
       */
      openContractsNotSentResults: (eventId: string) => {
        if (!eventId) return;
        loadResultsContractsNotSents({ eventId, notifyWhenEmpty: true });
      },

      watchCentralContractNotification,

      closeContractsNotSentModal: () => {
        patchState(store, { isContractsNotSentModalOpen: false, contractsNotSentResults: null, ...setPropInit('loadResultsContractsNotSents') });
      },

      openResolvePending: () => {
        // Mutually exclusive with the fix-registrations modal so they never overlap.
        patchState(store, { isResolvePendingOpen: true, isFixRegistrationsOpen: false });
      },

      closeResolvePending: () => {
        patchState(store, { isResolvePendingOpen: false });
      },

      openFixRegistrations: () => {
        // Mutually exclusive with the resolve-pending drawer so they never overlap.
        patchState(store, { isFixRegistrationsOpen: true, isResolvePendingOpen: false });
      },

      closeFixRegistrations: () => {
        patchState(store, { isFixRegistrationsOpen: false });
      },

      openDialogResendEmail: () => {
        dialogStore.openDialog({
          title: 'CONTRACTS.RESEND_EMAIL.MODAL_TITLE',
          message: 'CONTRACTS.RESEND_EMAIL.MODAL_SUBTITLE',
          closeOnSignal: [store.sendToSignsNotSendsLoaded, store.sendToSignsNotSendsError],
          primaryButton: {
            label: 'COMPONENTS.BUTTON.CANCEL',
            action: () => {
              dialogStore.closeDialog();
            },
          },
          secondaryButton: {
            label: 'CONTRACTS.RESEND_EMAIL.SUBMIT',
            type: 'primary',
            loadingOnsignal: store.sendToSignsNotSendsLoading,
            action: () => {
              sendToSignsNotSends();
            },
          },
        });
      },

      sendToSignsNotSends,

      sendToSignsPending,

      openDialogResendReminders: () => {
        dialogStore.openDialog({
          title: 'CONTRACTS.RESEND_REMINDERS.MODAL_TITLE',
          message: 'CONTRACTS.RESEND_REMINDERS.MODAL_SUBTITLE',
          closeOnSignal: [store.sendRemindersUnsignedContractsLoaded, store.sendRemindersUnsignedContractsError],
          primaryButton: {
            label: 'COMPONENTS.BUTTON.CANCEL',
            action: () => {
              dialogStore.closeDialog();
            },
          },
          secondaryButton: {
            label: 'CONTRACTS.RESEND_REMINDERS.SUBMIT',
            type: 'primary',
            loadingOnsignal: store.sendRemindersUnsignedContractsLoading,
            action: () => {
              sendRemindersUnsignedContracts();
            },
          },
        });
      },

      downloadContractById: rxMethod<{ StorageObjectId: string; Description: string }>(pipe(
        switchMap(({ StorageObjectId, Description }) => guardPermission(
          PermissionNumber.ContratoObterPorId,
          'CONTRACT_VIEW',
          () => errorStore.handleError({ message: 'ALERT.ERROR_401' }),
          () => storageService.getObjectUrl(StorageObjectId).pipe(
            tapResponse({
              next: (url) => {
                downloadService.downloadUrlObjectFile(url[StorageObjectId], Description);
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
              }
            }),
          )
        ))
      )),

      loadContracts(filters?: any): void {
        store.contractsDataSource()?.paginate(false);
        store.contractsDataSource()?.filter(filters?.length ? filters : null);
        store.contractsDataSource()?.load().then((data: IContractOdata[]) => {
          const contracts = data.map(contract => getContractWithSigners(contract));
          patchState(store, { contracts });
        });
      },

      closeContract,

      openDialogCloseContract: (payload: IClosedContract) => {
        dialogStore.openDialog({
          title: 'CONTRACTS.DIALOG.CLOSE_CONTRACT.TITLE',
          message: 'CONTRACTS.DIALOG.CLOSE_CONTRACT.MESSAGE',
          closeOnSignal: [store.closeContractLoaded, store.closeContractError],
          primaryButton: {
            label: 'COMPONENTS.BUTTON.CANCEL',
            action: () => {
              dialogStore.closeDialog();
            },
          },
          secondaryButton: {
            label: 'CONTRACTS.DIALOG.CLOSE_CONTRACT.YES_CLOSE',
            type: 'destructive',
            loadingOnsignal: store.closeContractLoading,
            action: () => {
              closeContract(payload);
            },
          },
        })
      },
      loadStatusCounts: rxMethod<string | void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('loadStatusCounts') })),
        switchMap((gridFilter) => {
          if (!gridFilter) {
            patchState(store, { statusCounts: null, ...setPropLoaded('loadStatusCounts') });
            return EMPTY;
          }
          const combine = (statusFilter: string) => `(${statusFilter}) and (${gridFilter})`;
          const today = DateTime.now().startOf('day');
          const todayIso = `${today.toISODate()}T00:00:00Z`;
          const in7DaysIso = `${today.plus({ days: 7 }).toISODate()}T23:59:59Z`;
          const in15DaysIso = `${today.plus({ days: 15 }).toISODate()}T23:59:59Z`;

          const activeBase = `Status eq 'signed' and StartedOn lt ${todayIso} and FinishedOn gt ${todayIso}`;

          return forkJoin({
            total: cService.getCount(gridFilter as string),
            active: cService.getCount(combine(activeBase)),
            signed: cService.getCount(combine("contains(tolower(Status), 'signed')")),
            signing: cService.getCount(combine("contains(tolower(Status), 'signing')")),
            closed: cService.getCount(combine("contains(tolower(Status), 'closed')")),
            expired: cService.getCount(combine("contains(tolower(Status), 'expired')")),
            declined: cService.getCount(combine("contains(tolower(Status), 'declined')")),
            added: cService.getCount(combine("contains(tolower(Status), 'added')")),
            linktosigngenerated: cService.getCount(combine("(contains(tolower(Status), 'linktosigngenerated') or contains(tolower(Status), 'emailtosignsent'))")),
            expiringIn7Days: cService.getCount(combine(`${activeBase} and FinishedOn le ${in7DaysIso}`)),
            expiringIn15Days: cService.getCount(combine(`${activeBase} and FinishedOn le ${in15DaysIso}`)),
            pendingSignature: cService.getCount(combine("(contains(tolower(Status), 'signing') or contains(tolower(Status), 'added') or contains(tolower(Status), 'emailtosignsent') or contains(tolower(Status), 'linktosigngenerated'))")),
          }).pipe(
            tapResponse({
              next: (statusCounts) => patchState(store, { statusCounts, ...setPropLoaded('loadStatusCounts') }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('loadStatusCounts', error));
              }
            })
          )
        })
      )),
      loadGlobalStatusCounts: rxMethod<void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('loadGlobalStatusCounts') })),
        switchMap(() => {
          const today = DateTime.now().startOf('day');
          const todayIso = `${today.toISODate()}T00:00:00Z`;
          const in7DaysIso = `${today.plus({ days: 7 }).toISODate()}T23:59:59Z`;
          const in15DaysIso = `${today.plus({ days: 15 }).toISODate()}T23:59:59Z`;

          const activeBase = `Status eq 'signed' and StartedOn lt ${todayIso} and FinishedOn gt ${todayIso}`;

          return forkJoin({
            total: cService.getCount(),
            active: cService.getCount(activeBase),
            signed: cService.getCount("contains(tolower(Status), 'signed')"),
            signing: cService.getCount("contains(tolower(Status), 'signing')"),
            closed: cService.getCount("contains(tolower(Status), 'closed')"),
            expired: cService.getCount("contains(tolower(Status), 'expired')"),
            declined: cService.getCount("contains(tolower(Status), 'declined')"),
            added: cService.getCount("contains(tolower(Status), 'added')"),
            linktosigngenerated: cService.getCount("(contains(tolower(Status), 'linktosigngenerated') or contains(tolower(Status), 'emailtosignsent'))"),
            expiringIn7Days: cService.getCount(`${activeBase} and FinishedOn le ${in7DaysIso}`),
            expiringIn15Days: cService.getCount(`${activeBase} and FinishedOn le ${in15DaysIso}`),
            pendingSignature: cService.getCount("(contains(tolower(Status), 'signing') or contains(tolower(Status), 'added') or contains(tolower(Status), 'emailtosignsent') or contains(tolower(Status), 'linktosigngenerated'))"),
            addedWithoutError: cService.getCount("Status eq 'added' and (ErrorMessage eq null or ErrorMessage eq '')"),
            addedWithError: cService.getCount("Status eq 'added' and ErrorMessage ne null and ErrorMessage ne ''"),
            awaitingSignature: cService.getCount("(contains(tolower(Status), 'linktosigngenerated') or contains(tolower(Status), 'emailtosignsent') or contains(tolower(Status), 'signing'))"),
          }).pipe(
            tapResponse({
              next: (globalStatusCounts) => patchState(store, { globalStatusCounts, ...setPropLoaded('loadGlobalStatusCounts') }),
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('loadGlobalStatusCounts', error));
              }
            })
          )
        })
      )),
      loadBlockedContracts: rxMethod<void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('loadBlockedContracts') })),
        switchMap(() => cService.getBlockedContracts().pipe(
          tapResponse({
            next: (blockedContracts) => patchState(store, { blockedContracts, ...setPropLoaded('loadBlockedContracts') }),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadBlockedContracts', error));
            }
          })
        ))
      )),
      /** Loads a recipient person (no includes) so the quick-fix flow can read their current Email. */
      loadRecipientProfile: rxMethod<string>(pipe(
        filter((personId) => !!personId),
        tap(() => patchState(store, { ...setPropLoading('loadRecipientProfile') })),
        switchMap((personId) => profileService.getPersonWithSpecificIncludes(personId, []).pipe(
          tapResponse({
            next: (recipientProfile) => patchState(store, { recipientProfile, ...setPropLoaded('loadRecipientProfile') }),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadRecipientProfile', error));
            }
          })
        ))
      )),
      /**
       * Assinaturas ativas da pessoa, para o lookup "Vincular a assinatura" do
       * modal de novo contrato.
       *
       * Usa o endpoint REST em vez de um `$filter` na view OData `Subscriptions`
       * porque só ele enxerga dependente: o `$metadata` da `SubscriptionView`
       * não tem `IsDependent` / `HolderPersonId`, então o dependente não possui
       * linha própria lá. O "ativo" também passa a ser decidido pelo backend,
       * o que evita reconstruir a janela de vigência no front (e o campo
       * `EndOnUtc` é `Nullable="false"`, então comparar com null derruba a query).
       */
      loadPersonSubscriptions: rxMethod<string | null | undefined>(pipe(
        tap((personId) => {
          if (!personId) {
            patchState(store, { personSubscriptions: null }, setPropInit('loadPersonSubscriptions'));
          }
        }),
        filter((personId): personId is string => !!personId),
        tap(() => patchState(store, { ...setPropLoading('loadPersonSubscriptions') })),
        switchMap((personId) => profileSubscriptionService.getSubscriptionsByPerson(personId, ESubscriptionsByPersonFieldFilter.Active).pipe(
          tapResponse({
            next: (personSubscriptions) => patchState(store, { personSubscriptions, ...setPropLoaded('loadPersonSubscriptions') }),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, { personSubscriptions: null, ...setPropError('loadPersonSubscriptions', error) });
            }
          })
        ))
      )),
      loadModelRawData: rxMethod<string | void>(pipe(
        tap(() => patchState(store, { ...setPropLoading('loadModelRawData') })),
        switchMap((gridFilter) => cService.getContractsModelSummary(gridFilter as string).pipe(
          tapResponse({
            next: (modelRawData) => patchState(store, { modelRawData, ...setPropLoaded('loadModelRawData') }),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError('loadModelRawData', error));
            }
          })
        ))
      )),
    }
  }
  ),
  withHooks((store) => {
    const clientNotificationStore = inject(ClientNotificationStore);
    return {
      onInit() {
        store.watchCentralContractNotification(clientNotificationStore?.notifications);
      },
    };
  }),
)
